import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useSocket } from '../contexts/SocketContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { UserProfilePopup } from './UserProfilePopup';
import { Search } from 'lucide-react';

const statusColors = {
  online: 'bg-green-500',
  idle: 'bg-yellow-500',
  dnd: 'bg-red-500',
  offline: 'bg-zinc-600',
};

export const MemberSidebar = () => {
  const { members, roles } = useApp();
  const { presence } = useSocket();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [profileUserId, setProfileUserId] = useState(null);

  const membersWithPresence = members.map(m => ({
    ...m,
    status: presence[m.user_id] || m.status || 'offline'
  }));

  const filtered = search
    ? membersWithPresence.filter(m =>
        m.username?.toLowerCase().includes(search.toLowerCase()) ||
        m.display_name?.toLowerCase().includes(search.toLowerCase())
      )
    : membersWithPresence;

  const online = filtered.filter(m => m.status !== 'offline');
  const offline = filtered.filter(m => m.status === 'offline');

  return (
    <div className="w-60 h-full bg-[#0e1015] border-l border-white/5 flex flex-col" data-testid="member-sidebar">
      <div className="p-2 border-b border-white/5">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
          <Input placeholder={t('app.searchMembers')} value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 bg-[#0b0d10] border-white/5 text-xs" data-testid="member-search" />
        </div>
      </div>

      <ScrollArea className="flex-1 px-2 py-2">
        {online.length > 0 && (
          <div className="mb-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 px-1 mb-1">
              {t('app.online')} — {online.length}
            </p>
            {online.map(m => <MemberItem key={m.user_id} member={m} onClick={() => setProfileUserId(m.user_id)} />)}
          </div>
        )}
        {offline.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 px-1 mb-1">
              {t('app.offline')} — {offline.length}
            </p>
            {offline.map(m => <MemberItem key={m.user_id} member={m} onClick={() => setProfileUserId(m.user_id)} />)}
          </div>
        )}
      </ScrollArea>

      <UserProfilePopup userId={profileUserId} open={!!profileUserId} onClose={() => setProfileUserId(null)} />
    </div>
  );
};

const MemberItem = ({ member, onClick }) => {
  const display = member.nickname || member.display_name || member.username;
  const roleColor = getRoleColor(member.roles);

  return (
    <button onClick={onClick} className="flex items-center gap-2.5 w-full px-2 py-1.5 rounded hover:bg-white/5 transition-colors group"
      data-testid={`member-${member.user_id}`}>
      <div className="relative flex-shrink-0">
        <Avatar className="h-8 w-8">
          <AvatarImage src={member.avatar} />
          <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">{display[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0e1015] ${statusColors[member.status] || statusColors.offline}`} />
      </div>
      <div className="min-w-0 flex-1 text-left">
        <p className="text-[13px] font-medium truncate" style={{ color: member.status === 'offline' ? '#71717a' : roleColor }}>
          {display}
        </p>
      </div>
    </button>
  );
};

function getRoleColor(roles) {
  if (!roles || roles.length === 0) return '#e4e4e7';
  if (roles.includes('role_owner')) return '#FFD700';
  if (roles.includes('role_admin')) return '#E74C3C';
  if (roles.includes('role_mod')) return '#3498DB';
  if (roles.includes('role_vip')) return '#9B59B6';
  if (roles.includes('role_active')) return '#E67E22';
  return '#e4e4e7';
}
